import { useState, useEffect, useCallback,useRef } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL } from '../constant/api'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'
const money   = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })} RWF` : '—'

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

// ── Order status config ───────────────────────────────────────────────────────
const ORDER_STATUS = {
  pending:    { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Pending'    },
  confirmed:  { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6', label: 'Confirmed'  },
  processing: { bg: '#f5f3ff', color: '#7c3aed', dot: '#8b5cf6', label: 'Processing' },
  shipped:    { bg: '#f0f9ff', color: '#0369a1', dot: '#0ea5e9', label: 'Shipped'    },
  delivered:  { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Delivered'  },
  cancelled:  { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Cancelled'  },
  returned:   { bg: '#fff7ed', color: '#c2410c', dot: '#f97316', label: 'Returned'   },
}
const os = (s) => ORDER_STATUS[s?.toLowerCase()] ?? { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: s || '—' }

// Timeline steps
const TIMELINE = ['pending','confirmed','processing','shipped','delivered']

// ── Shared ────────────────────────────────────────────────────────────────────
const SS = {
  select: { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" },
}


// ── Parse product image ───────────────────────────────────────────────────────
// images is stored as a JSON array string: ["D:\\projects\\...\\file.jpg"]
// We extract the filename and build a URL to the uploads endpoint
const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL

const parseImage = (images) => {
  try {
    const arr = typeof images === 'string' ? JSON.parse(images) : images
    if (!Array.isArray(arr) || arr.length === 0) return null
    const raw = arr[0]
    if (typeof raw === 'string' && raw.startsWith('http')) return raw
    const filename = raw.replace(/\\/g, '/').split('/').pop()
    return `${API_BASE}/${filename}`
  } catch {
    return null
  }
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function OrderBadge({ status }) {
  const s = os(status)
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:s.bg, color:s.color, fontSize:10, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", padding:'3px 8px', borderRadius:4, textTransform:'uppercase', whiteSpace:'nowrap' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:s.dot }} />{s.label}
    </span>
  )
}

// ── Order Timeline ────────────────────────────────────────────────────────────
function OrderTimeline({ status }) {
  const current = TIMELINE.indexOf(status?.toLowerCase())
  const isCancelled = status?.toLowerCase() === 'cancelled'
  const isReturned  = status?.toLowerCase() === 'returned'

  if (isCancelled || isReturned) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0', marginBottom:16 }}>
        <div style={{ width:32, height:32, borderRadius:'50%', background:'#fef2f2', border:'2px solid #ef4444', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>
          {isCancelled ? '✕' : '↩'}
        </div>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#b91c1c' }}>{isCancelled ? 'Order Cancelled' : 'Order Returned'}</div>
          <div style={{ fontSize:11, color:'#9a9a94' }}>This order was {isCancelled ? 'cancelled' : 'returned'}</div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position:'relative', padding:'12px 0', marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', position:'relative' }}>
        {/* Track line */}
        <div style={{ position:'absolute', top:14, left:14, right:14, height:2, background:'#e9e9e4', zIndex:0 }} />
        <div style={{ position:'absolute', top:14, left:14, height:2, background:'#e8440a', zIndex:0, width: current >= 0 ? `${(current / (TIMELINE.length - 1)) * (100 - 28/TIMELINE.length)}%` : '0%', transition:'width 0.4s' }} />

        {TIMELINE.map((step, i) => {
          const done   = i <= current
          const active = i === current
          const s      = os(step)
          return (
            <div key={step} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5, zIndex:1 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background: done ? s.dot : '#fff', border:`2px solid ${done ? s.dot : '#e9e9e4'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color: done ? '#fff' : '#9a9a94', fontWeight:800, transition:'all 0.3s', boxShadow: active ? `0 0 0 4px ${s.dot}22` : 'none' }}>
                {done && i < current ? '✓' : i + 1}
              </div>
              <div style={{ fontSize:9, fontWeight: active ? 700 : 500, color: active ? s.color : '#9a9a94', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:"'JetBrains Mono',monospace" }}>{step}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Order Detail Drawer ───────────────────────────────────────────────────────
function OrderDrawer({ order, onClose, onStatusChange }) {
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)
  const [tab,        setTab]        = useState('details')
  const [updating,   setUpdating]   = useState(false)
  const [refund,        setRefund]        = useState(null)
  const [refundLoad,    setRefundLoad]    = useState(false)
  const [refundReason,  setRefundReason]  = useState('')
  const [refundDetails, setRefundDetails] = useState('')
  const [refundFiles,   setRefundFiles]   = useState([])
  const [refundSubmit,  setRefundSubmit]  = useState(false)
  const [refundError,   setRefundError]   = useState('')
  const [refundSuccess, setRefundSuccess] = useState('')
  const [adminNote,     setAdminNote]     = useState('')
  const [adminAction,   setAdminAction]   = useState(false)

  useEffect(() => {
    if (!order) return
    setTab('details'); setDetail(null)
    setDetailLoad(true)
    api.get(`/orders/admin/orders/${order.order_id}`)
      .then(res => setDetail(res.data.order))
      .catch(() => setDetail(order))
      .finally(() => setDetailLoad(false))
    setRefund(null); setRefundError(''); setRefundSuccess('')
    setRefundReason(''); setRefundDetails(''); setRefundFiles([]); setAdminNote('')
    setRefundLoad(true)
    api.get(`/refunds/admin/order/${order.order_id}`)
      .then(res => setRefund(res.data.refund || null))
      .catch(() => setRefund(null))
      .finally(() => setRefundLoad(false))
  }, [order?.order_id])

  if (!order) return null
  const d  = detail || order
  const bg = avatarBg(d.buyer_name || d.buyer_phone || '')

  const handleStatusChange = async (newStatus) => {
    setUpdating(true)
    try {
      await api.patch(`/orders/admin/orders/${d.order_id}/status`, { status: newStatus })
      setDetail(prev => ({ ...prev, status: newStatus }))
      onStatusChange(d.order_id, newStatus)
    } catch { alert('Failed to update status') }
    finally { setUpdating(false) }
  }


  const handleRefundSubmit = async () => {
    if (!refundReason) { setRefundError('Please select a reason'); return }
    if (refundReason === 'other' && !refundDetails.trim()) { setRefundError('Details required for "Other"'); return }
    if (!['not_shipped','seller_cancel','seller_cancelled'].includes(refundReason) && !refundFiles.some(f => f.type.startsWith('image/'))) {
      setRefundError('At least one photo is required'); return
    }
    setRefundSubmit(true); setRefundError('')
    try {
      const form = new FormData()
      form.append('order_id', d.order_id)
      form.append('reason', refundReason)
      if (refundDetails) form.append('details', refundDetails)
      refundFiles.forEach(f => form.append('evidence', f))
      const res = await api.post('/refunds/request', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      setRefundSuccess(res.data.message || 'Refund request submitted.')
      const r2 = await api.get(`/refunds/admin/order/${d.order_id}`)
      setRefund(r2.data.refund || null)
    } catch (err) { setRefundError(err.response?.data?.error || 'Failed to submit') }
    finally { setRefundSubmit(false) }
  }

  const handleApprove = async () => {
    setAdminAction(true); setRefundError('')
    try {
      const res = await api.post(`/refunds/admin/${refund.refund_id}/approve`, { admin_note: adminNote })
      setRefund(prev => ({ ...prev, status: 'approved' }))
      // IremboPay SDK has no refund API — show instructions to process in portal
      const ref  = res.data.payment_ref
      const num  = res.data.payment_number
      const inst = res.data.instructions || 'Process in IremboPay portal manually.'
      setRefundSuccess(
        `Approved. Now process the reversal manually:\n` +
        `${inst}\n` +
        (ref ? `Payment Ref: ${ref}` : '') +
        (num ? `  · Phone: ${num}` : '')
      )
    } catch (err) { setRefundError(err.response?.data?.error || 'Failed to approve') }
    finally { setAdminAction(false) }
  }

  const handleReject = async () => {
    if (!adminNote.trim()) { setRefundError('Rejection reason is required'); return }
    setAdminAction(true); setRefundError('')
    try {
      await api.post(`/refunds/admin/${refund.refund_id}/reject`, { admin_note: adminNote })
      setRefundSuccess('Refund rejected.')
      setRefund(prev => ({ ...prev, status: 'rejected', admin_note: adminNote }))
    } catch (err) { setRefundError(err.response?.data?.error || 'Failed to reject') }
    finally { setAdminAction(false) }
  }

  const handleMarkRefunded = async () => {
    setAdminAction(true); setRefundError('')
    try {
      await api.post(`/refunds/admin/${refund.refund_id}/mark-refunded`, { admin_note: adminNote })
      setRefundSuccess('Marked as refunded.')
      setRefund(prev => ({ ...prev, status: 'refunded' }))
      onStatusChange(d.order_id, 'cancelled')
    } catch (err) { setRefundError(err.response?.data?.error || 'Failed') }
    finally { setAdminAction(false) }
  }

  const nextStatus = {
    pending:    'confirmed',
    confirmed:  'processing',
    processing: 'shipped',
    shipped:    'delivered',
  }[d.status?.toLowerCase()]

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.18)', zIndex:200 }} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:520, background:'#fff', zIndex:201, display:'flex', flexDirection:'column', boxShadow:'-4px 0 40px rgba(0,0,0,0.12)', animation:'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:800, flexShrink:0 }}>
            {initials(d.buyer_name || d.buyer_phone)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:800 }}>Order #{d.order_id}</div>
            <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginTop:2 }}>
              {d.buyer_name || 'Anonymous'} · {fmtTime(d.created_at)}
            </div>
          </div>
          <OrderBadge status={d.status} />
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94', marginLeft:6 }}>×</div>
        </div>

        {/* Timeline */}
        <div style={{ padding:'14px 22px 0', borderBottom:'1px solid #e9e9e4' }}>
          <OrderTimeline status={d.status} />
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #e9e9e4', padding:'0 22px' }}>
          {[['details','Details'],['items','Items'],['delivery','Delivery'],['refund','Refund']].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding:'10px 14px', border:'none', background:'none', cursor:'pointer', fontSize:12, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif", color:tab===id?'#111317':'#9a9a94', borderBottom:`2px solid ${tab===id?'#e8440a':'transparent'}`, marginBottom:-1 }}>{label}</button>
          ))}
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:22 }}>
          {detailLoad && <div style={{ textAlign:'center', color:'#9a9a94', fontSize:13, padding:'32px 0' }}>Loading…</div>}

          {/* ── Details Tab ── */}
          {!detailLoad && tab === 'details' && (
            <>
              {[
                ['Order ID',        `#${d.order_id}`],
                ['Buyer',           d.buyer_name   || '—'],
                ['Buyer Phone',     d.buyer_phone  || '—'],
                ['Shop',            d.shop_name    || '—'],
                ['Order Status',    <OrderBadge status={d.status} />],
                ['Payment Status',  d.payment_status || '—'],
                ['Payment Method',  d.payment_method ? d.payment_method.toUpperCase() : '—'],
                ['Payment Ref',     d.payment_ref  || '—'],
                ['Subtotal',        money(d.subtotal || d.total_amount)],
                ['Delivery Fee',    money(d.delivery_fee)],
                ['Total Amount',    money(d.total_amount + d.delivery_fee)],
                ['Placed',          fmtTime(d.created_at)],
                ['Updated',         fmtTime(d.updated_at)],
                ...(d.buyer_note ? [['Buyer Note', d.buyer_note]] : []),
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0', borderBottom:'1px solid #f3f3f0' }}>
                  <span style={{ fontSize:12, color:'#9a9a94', fontWeight:600 }}>{k}</span>
                  <span style={{ fontSize:12, fontWeight:700, fontFamily:typeof v==='string'?"'JetBrains Mono',monospace":'inherit', color:'#111317', textAlign:'right', maxWidth:270, wordBreak:'break-all' }}>{v}</span>
                </div>
              ))}
            </>
          )}

          {/* ── Items Tab ── */}
          {!detailLoad && tab === 'items' && (
            !d.items?.length
              ? <div style={{ textAlign:'center', color:'#9a9a94', fontSize:13, padding:'40px 0' }}>No items found.</div>
              : <>
                  {d.items.map((item, i) =>{
                   const imgUrl = parseImage(item.images)
                  return(
                    <div key={item.prod_id || i} style={{ padding:'12px 0', borderBottom:'1px solid #f3f3f0' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ width:44, height:44, borderRadius:8, background:'#f3f3f0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0, overflow:'hidden' }}>
                          {imgUrl ? <img src={imgUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'} /> : '📦'}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name || item.prod_name}</div>
                          {item.variant_label && (
                            <div style={{ fontSize:10, fontWeight:700, color:'#7c3aed', background:'#f5f3ff', borderRadius:4, padding:'1px 6px', display:'inline-flex', marginBottom:2, fontFamily:"'JetBrains Mono',monospace" }}>
                              {item.variant_label}
                            </div>
                          )}
                          <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{item.shop_name || '—'} · ×{item.quantity}</div>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:13, fontWeight:800, fontFamily:"'JetBrains Mono',monospace" }}>{money(item.unit_price * item.quantity)}</div>
                          <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{money(item.unit_price)} each</div>
                        </div>
                      </div>
                      {/* ── Delivery zone / fee per item ── */}
                      {(() => {
                        const fee      = Number(item.delivery_fee || 0)
                        const fallback = Number(item.shop_fallback_fee || 0)
                        const hasZone  = !!item.zone_name
                        const zoneLoc  = [item.zone_city, item.zone_district].filter(Boolean).join(', ')
                        if (hasZone) return (
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, padding:'5px 10px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6 }}>
                            <span style={{ fontSize:10, fontWeight:700, color:'#15803d', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Zone</span>
                            <span style={{ fontSize:11, fontWeight:700, color:'#111317' }}>{item.zone_name}</span>
                            {zoneLoc && <span style={{ fontSize:10, color:'#6b7280', fontFamily:"'JetBrains Mono',monospace" }}>· {zoneLoc}</span>}
                            <span style={{ marginLeft:'auto', fontSize:11, fontWeight:800, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>{money(fee)}</span>
                          </div>
                        )
                        if (fee > 0) return (
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, padding:'5px 10px', background:'#fff3f0', border:'1px solid #fdd0c0', borderRadius:6 }}>
                            <span style={{ fontSize:10, fontWeight:700, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Fallback Delivery Fee</span>
                            <span style={{ fontSize:11, color:'#6b7280' }}>No matching zone — fallback fee applied</span>
                            <span style={{ marginLeft:'auto', fontSize:11, fontWeight:800, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace" }}>{money(fee)}</span>
                          </div>
                        )
                        if (fallback > 0) return (
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, padding:'5px 10px', background:'#fff3f0', border:'1px solid #fdd0c0', borderRadius:6 }}>
                            <span style={{ fontSize:10, fontWeight:700, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Fallback Delivery Fee</span>
                            <span style={{ fontSize:11, color:'#6b7280' }}>Shop fallback: {money(fallback)}</span>
                          </div>
                        )
                        return (
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, padding:'5px 10px', background:'#f9f9f7', border:'1px solid #e9e9e4', borderRadius:6 }}>
                            <span style={{ fontSize:10, fontWeight:700, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", textTransform:'uppercase' }}>Delivery</span>
                            <span style={{ fontSize:11, color:'#9a9a94' }}>Free</span>
                          </div>
                        )
                      })()}
                    </div>
                  )})}
                  {/* Order total */}
                  <div style={{ marginTop:12, background:'#f9f9f7', borderRadius:8, padding:'12px 14px' }}>
                    {[
                      ['Subtotal',     money(d.subtotal || d.total_amount)],
                      ['Delivery Fee', money(d.delivery_fee)],
                      ['Total',        money((d.total_amount || 0) + (d.delivery_fee || 0))],
                    ].map(([k,v]) => (
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #efefed' }}>
                        <span style={{ fontSize:12, color:'#6b7280' }}>{k}</span>
                        <span style={{ fontSize:12, fontWeight:600, fontFamily:"'JetBrains Mono',monospace" }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0' }}>
                      <span style={{ fontSize:13, fontWeight:800 }}>Total</span>
                      <span style={{ fontSize:13, fontWeight:800, fontFamily:"'JetBrains Mono',monospace", color:'#111317' }}>{money((d.total_amount || 0) + (d.delivery_fee || 0))}</span>
                    </div>
                  </div>
                </>
          )}


          {/* ── Refund Tab ── */}
          {tab === 'refund' && (
            <div>
              {refundLoad && <div style={{textAlign:'center',color:'#9a9a94',fontSize:13,padding:'32px 0'}}>Loading refund info…</div>}

              {/* Existing refund record */}
              {!refundLoad && refund && (() => {
                const RS = {
                  pending:    {bg:'#fffbeb',color:'#b45309',label:'Pending Review'},
                  processing: {bg:'#eff6ff',color:'#1d4ed8',label:'Processing'},
                  approved:   {bg:'#f0fdf4',color:'#15803d',label:'Approved'},
                  refunded:   {bg:'#f0fdf4',color:'#15803d',label:'Refunded ✓'},
                  rejected:   {bg:'#fef2f2',color:'#b91c1c',label:'Rejected'},
                  cancelled:  {bg:'#f3f4f6',color:'#6b7280',label:'Cancelled'},
                }
                const rs = RS[refund.status] || RS.pending
                return (
                  <div>
                    {/* Status banner */}
                    <div style={{background:rs.bg,border:`1px solid ${rs.color}33`,borderRadius:10,padding:'12px 16px',marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:rs.color,textTransform:'uppercase',letterSpacing:'0.07em',fontFamily:"'JetBrains Mono',monospace"}}>Refund Status</div>
                        <div style={{fontSize:16,fontWeight:800,color:rs.color,marginTop:3}}>{rs.label}</div>
                      </div>
                      <div style={{fontSize:11,color:'#9a9a94',fontFamily:"'JetBrains Mono',monospace",textAlign:'right'}}>
                        <div>#{refund.refund_id}</div>
                        <div style={{marginTop:2}}>{fmtTime(refund.created_at)}</div>
                      </div>
                    </div>

                    {/* Details */}
                    {[
                      ['Reason',       refund.reason_label || refund.reason],
                      ['Items Subtotal', money(refund.amount)],
                      ['Delivery Fee',   money(d.delivery_fee || 0)],
                      ['Total Refund',   (() => {
                        const total = Number(refund.amount || 0) + Number(d.delivery_fee || 0)
                        return <span style={{fontWeight:800,color:'#b91c1c'}}>{money(total)}</span>
                      })()],
                      ['Details',      refund.details || '—'],
                      ['Payment',      refund.payment_method?.toUpperCase() || '—'],
                      ['Refund Ref',   refund.refund_ref || '—'],
                      ['Provider Msg', refund.provider_message || '—'],
                      ['Admin Note',   refund.admin_note || '—'],
                    ].map(([k,v]) => (
                      <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',padding:'8px 0',borderBottom:'1px solid #f3f3f0',gap:12}}>
                        <span style={{fontSize:12,color:'#9a9a94',fontWeight:600,flexShrink:0}}>{k}</span>
                        <span style={{fontSize:12,fontWeight:700,color:'#111317',textAlign:'right',wordBreak:'break-word',maxWidth:280,fontFamily:"'JetBrains Mono',monospace"}}>{v}</span>
                      </div>
                    ))}

                    {/* Evidence */}
                    {refund.evidence?.length > 0 && (
                      <div style={{marginTop:14}}>
                        <div style={{fontSize:11,fontWeight:700,color:'#9a9a94',textTransform:'uppercase',letterSpacing:'0.07em',fontFamily:"'JetBrains Mono',monospace",marginBottom:8}}>Evidence ({refund.evidence.length})</div>
                        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                          {refund.evidence.map((ev,i) => (
                            ev.type === 'photo'
                              ? <a key={i} href={ev.url} target="_blank" rel="noreferrer">
                                  <img src={ev.url} alt={`ev${i}`} style={{width:72,height:72,borderRadius:8,objectFit:'cover',border:'1px solid #e9e9e4'}} />
                                </a>
                              : <a key={i} href={ev.url} target="_blank" rel="noreferrer" style={{width:72,height:72,borderRadius:8,background:'#f3f3f0',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,border:'1px solid #e9e9e4',textDecoration:'none'}}>🎥</a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Admin note input */}
                    {['pending','approved','processing'].includes(refund.status) && (
                      <div style={{marginTop:16,background:'#f9f9f7',borderRadius:10,padding:'14px 16px',border:'1px solid #e9e9e4'}}>
                        <div style={{fontSize:11,fontWeight:700,color:'#9a9a94',textTransform:'uppercase',letterSpacing:'0.07em',fontFamily:"'JetBrains Mono',monospace",marginBottom:8}}>
                          {refund.status === 'pending' ? 'Admin Decision' : 'Confirm Refund Sent'}
                        </div>
                        {refund.status === 'pending' && (
                          <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)}
                            placeholder="Note for buyer (required for rejection)…"
                            style={{width:'100%',borderRadius:8,border:'1px solid #e9e9e4',padding:'9px 12px',fontSize:12,fontFamily:"'Plus Jakarta Sans',sans-serif",resize:'vertical',minHeight:64,outline:'none',boxSizing:'border-box',marginBottom:10}} />
                        )}

                        {refund.status === 'pending' && (
                          <div style={{display:'flex',gap:8}}>
                            <button onClick={handleApprove} disabled={adminAction}
                              style={{flex:1,height:38,borderRadius:8,border:'none',background:adminAction?'#f0c4b4':'#e8440a',color:'#fff',fontSize:12,fontWeight:700,cursor:adminAction?'not-allowed':'pointer',fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                              {adminAction ? 'Approving…' : '✓ Approve (then process in portal)'}
                            </button>
                            <button onClick={handleReject} disabled={adminAction}
                              style={{flex:1,height:38,borderRadius:8,border:'1px solid #fecaca',background:'#fef2f2',color:'#b91c1c',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                              ✕ Reject
                            </button>
                          </div>
                        )}

                        {['approved','processing'].includes(refund.status) && (
                          <div>
                            {/* Portal instructions */}
                            <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:8,padding:'10px 13px',marginBottom:10,fontSize:11,color:'#0369a1',lineHeight:1.6}}>
                              📋 Go to <b>dashboard.irembopay.com</b> → Transactions → search <b>{refund.payment_ref || refund.payment_method?.toUpperCase() || 'payment ref'}</b> → click <b>Reverse</b>.
                              Then enter the ref below and confirm.
                            </div>
                            <input
                              value={adminNote}
                              onChange={e => setAdminNote(e.target.value)}
                              placeholder="IremboPay reversal ref (e.g. TXN-XXXXXXX)…"
                              style={{width:'100%',height:38,borderRadius:8,border:'1px solid #e9e9e4',padding:'0 12px',fontSize:12,fontFamily:"'Plus Jakarta Sans',sans-serif",outline:'none',boxSizing:'border-box',marginBottom:10}}
                            />
                            <button onClick={handleMarkRefunded} disabled={adminAction}
                              style={{width:'100%',height:42,borderRadius:8,border:'none',background:adminAction?'#a3d9b6':'#15803d',color:'#fff',fontSize:13,fontWeight:700,cursor:adminAction?'not-allowed':'pointer',fontFamily:"'Plus Jakarta Sans',sans-serif",display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                              {adminAction
                                ? 'Confirming…'
                                : <>
                                    <span>✓</span>
                                    <span>Confirm Refund of {money(Number(refund.amount||0) + Number(d.delivery_fee||0))}</span>
                                  </>
                              }
                            </button>
                          </div>
                        )}

                        {refundError   && <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:7,padding:'9px 13px',fontSize:12,color:'#b91c1c',fontWeight:600,marginTop:10}}>⚠ {refundError}</div>}
                        {refundSuccess && <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:7,padding:'9px 13px',fontSize:12,color:'#15803d',fontWeight:600,marginTop:10}}>✓ {refundSuccess}</div>}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* No refund — show request form */}
              {!refundLoad && !refund && (() => {
                const status = d.status?.toLowerCase()
                const isPaid = d.payment_status === 'paid'
                const canRefund = isPaid && !['cancelled','returned'].includes(status)
                // Note: cancelled orders auto-get a refund via seller_cancel reason
                // The refund record will show in the existing-refund view above
                if (!canRefund) return (
                  <div style={{background:'#f9f9f7',border:'1px solid #e9e9e4',borderRadius:10,padding:'28px',textAlign:'center'}}>
                    <div style={{fontSize:26,marginBottom:8}}>🔒</div>
                    <div style={{fontSize:13,fontWeight:700,color:'#6b7280',marginBottom:4}}>Refund Not Available</div>
                    <div style={{fontSize:12,color:'#9a9a94'}}>{!isPaid ? 'Order has not been paid.' : `Cannot refund a ${d.status} order.`}</div>
                  </div>
                )
                return (
                  <div>
                    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:12,color:'#92400e'}}>
                      ℹ Submit a refund request on behalf of the buyer. The refund is tracked against their order.
                    </div>

                    <div style={{marginBottom:12}}>
                      <label style={{display:'block',fontSize:11,fontWeight:700,color:'#111317',textTransform:'uppercase',letterSpacing:'0.07em',fontFamily:"'JetBrains Mono',monospace",marginBottom:6}}>Reason *</label>
                      <select value={refundReason} onChange={e => setRefundReason(e.target.value)}
                        style={{width:'100%',height:40,borderRadius:8,border:'1px solid #e9e9e4',padding:'0 12px',fontSize:13,outline:'none',fontFamily:"'Plus Jakarta Sans',sans-serif",background:'#fff',cursor:'pointer',boxSizing:'border-box'}}>
                        <option value="">Select a reason…</option>
                        <option value="wrong_item">Wrong Item Delivered</option>
                        <option value="damaged">Damaged Product</option>
                        <option value="not_as_described">Not as Described</option>
                        <option value="not_shipped">Seller Fails to Ship</option>
                        <option value="seller_cancel">Seller Cancelled My Order</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div style={{marginBottom:12}}>
                      <label style={{display:'block',fontSize:11,fontWeight:700,color:'#111317',textTransform:'uppercase',letterSpacing:'0.07em',fontFamily:"'JetBrains Mono',monospace",marginBottom:6}}>
                        Details {refundReason === 'other' ? '*' : '(optional)'}
                      </label>
                      <textarea value={refundDetails} onChange={e => setRefundDetails(e.target.value)}
                        placeholder="Describe the issue in detail…"
                        style={{width:'100%',borderRadius:8,border:'1px solid #e9e9e4',padding:'9px 12px',fontSize:12,fontFamily:"'Plus Jakarta Sans',sans-serif",resize:'vertical',minHeight:64,outline:'none',boxSizing:'border-box'}} />
                    </div>

                    {refundReason && !['not_shipped','seller_cancel','seller_cancelled'].includes(refundReason) && (
                      <div style={{marginBottom:14}}>
                        <label style={{display:'block',fontSize:11,fontWeight:700,color:'#111317',textTransform:'uppercase',letterSpacing:'0.07em',fontFamily:"'JetBrains Mono',monospace",marginBottom:6}}>
                          Evidence Photos * <span style={{color:'#9a9a94',fontWeight:400}}>(min 1 photo)</span>
                        </label>
                        <input type="file" accept="image/*,video/*" multiple
                          onChange={e => setRefundFiles(Array.from(e.target.files))}
                          style={{fontSize:12,color:'#374151'}} />
                        {refundFiles.length > 0 && (
                          <div style={{marginTop:7,display:'flex',gap:6,flexWrap:'wrap'}}>
                            {refundFiles.map((f,i) => (
                              <div key={i} style={{fontSize:10,background:'#f3f3f0',borderRadius:6,padding:'3px 8px',color:'#374151'}}>{f.name.slice(0,20)}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {refundError   && <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:7,padding:'9px 13px',fontSize:12,color:'#b91c1c',fontWeight:600,marginBottom:10}}>⚠ {refundError}</div>}
                    {refundSuccess && <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:7,padding:'9px 13px',fontSize:12,color:'#15803d',fontWeight:600,marginBottom:10}}>✓ {refundSuccess}</div>}

                    <button onClick={handleRefundSubmit} disabled={refundSubmit || !refundReason}
                      style={{width:'100%',height:42,borderRadius:8,border:'none',background:!refundReason||refundSubmit?'#f0c4b4':'#e8440a',color:'#fff',fontSize:13,fontWeight:700,cursor:!refundReason||refundSubmit?'not-allowed':'pointer',fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                      {refundSubmit ? 'Submitting…' : `Request Refund — ${money(d.total_amount)}`}
                    </button>
                  </div>
                )
              })()}
            </div>
          )}

          {/* ── Delivery Tab ── */}
          {!detailLoad && tab === 'delivery' && (
            <div>
              {/* Delivery address */}
              <div style={{ background:'#f9f9f7', borderRadius:10, padding:'14px 16px', marginBottom:16, border:'1px solid #e9e9e4' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:8 }}>Delivery Address</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#111317', lineHeight:1.6 }}>
                  {d.delivery_address || 'No delivery address set'}
                </div>
              </div>

              {/* Self-delivery card */}
              {d.is_self_delivery && (
                <div style={{ background:'#fff3f0', border:'1px solid #fdd0c0', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#e8440a', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:10 }}>🛵 Self Delivery</div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:avatarBg(d.seller_name||d.shop_name||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800 }}>
                      {initials(d.seller_name || d.shop_name)}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700 }}>{d.seller_name || d.shop_name || 'Seller'}</div>
                      <div style={{ fontSize:11, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace" }}>{d.seller_phone || '—'}</div>
                    </div>
                  </div>
                  <div style={{ background:'#fff', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#92400e', lineHeight:1.6, border:'1px solid #fdd0c0' }}>
                    The seller is handling delivery themselves. Contact them directly if needed.
                  </div>
                  {[
                    ['Delivery Status', d.delivery_status || 'self_delivery'],
                    ['Shop',            d.shop_name || '—'],
                    ['Seller Phone',    d.seller_phone || '—'],
                    ['Delivered At',    d.delivered_at ? fmtTime(d.delivered_at) : '—'],
                  ].map(([k,v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(0,0,0,0.06)', marginTop:2 }}>
                      <span style={{ fontSize:11, color:'#e8440a', fontWeight:600 }}>{k}</span>
                      <span style={{ fontSize:11, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color:'#111317' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Driver */}
              {!d.is_self_delivery && (d.driver_name || d.driver_phone) ? (
                <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#15803d', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:10 }}>Assigned Driver</div>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:avatarBg(d.driver_name||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800 }}>
                      {initials(d.driver_name || d.driver_phone)}
                    </div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700 }}>{d.driver_name || 'Driver'}</div>
                      <div style={{ fontSize:11, color:'#15803d', fontFamily:"'JetBrains Mono',monospace" }}>{d.driver_phone || '—'}</div>
                    </div>
                  </div>
                  {[
                    ['Company',    d.driver_company || 'Unaffiliated'],
                    ['Vehicle',    d.vehicle_type   || '—'],
                    ['Plate',      d.vehicle_plate  || '—'],
                    ['Status',     d.delivery_status || '—'],
                    ['Delivered',  d.delivered_at ? fmtTime(d.delivered_at) : '—'],
                  ].map(([k,v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
                      <span style={{ fontSize:11, color:'#15803d', fontWeight:600 }}>{k}</span>
                      <span style={{ fontSize:11, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color:'#111317' }}>{v}</span>
                    </div>
                  ))}
                </div>
              ) : !d.is_self_delivery ? (
                <div style={{ background:'#fafaf8', border:'1px solid #e9e9e4', borderRadius:8, padding:'14px', marginBottom:16, fontSize:12, color:'#9a9a94', textAlign:'center' }}>
                  No driver assigned to this order yet.
                </div>
              ) : null}

              {/* Delivery status */}
              <div style={{ background:'#f9f9f7', borderRadius:8, padding:'12px 14px', border:'1px solid #e9e9e4' }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace", marginBottom:8 }}>Delivery Status</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{d.delivery_status || '—'}</div>
              </div>

              {/* Buyer note — shown for cancelled deliveries too */}
              {d.buyer_note && (
                <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, padding:'12px 14px', marginTop:12 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#0369a1', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }}>Buyer Note</div>
                  <div style={{ fontSize:13, color:'#0c4a6e', lineHeight:1.6 }}>{d.buyer_note}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — status actions */}
        <div style={{ padding:'14px 22px', borderTop:'1px solid #e9e9e4' }}>
          {nextStatus && (
            <button
              onClick={() => handleStatusChange(nextStatus)}
              disabled={updating}
              style={{ width:'100%', height:42, borderRadius:8, border:'none', background: updating ? '#f0c4b4' : '#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor: updating ? 'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:8 }}>
              {updating ? 'Updating…' : `Mark as ${nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)}`} →
            </button>
          )}
          {!['cancelled','returned','delivered'].includes(d.status?.toLowerCase()) && (
            <button
              onClick={() => handleStatusChange('cancelled')}
              disabled={updating}
              style={{ width:'100%', height:38, borderRadius:8, border:'1px solid #fecaca', background:'#fff5f5', color:'#b91c1c', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Cancel Order
            </button>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Orders() {
  const [orders,       setOrders]       = useState([])
  const now        = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().slice(0,10)

  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom,     setDateFrom]     = useState(monthStart)
  const [dateTo,       setDateTo]       = useState(monthEnd)
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [stats,        setStats]        = useState({ total:0, pending:0, processing:0, delivered:0, cancelled:0, revenue:0 })
  const [selected,     setSelected]     = useState(null)

  const PER_PAGE = 20
  const fetchIdRef = useRef(0)

  // Debounce search input → only fetch after 400ms of no typing
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchOrders = useCallback(async () => {
    const id = ++fetchIdRef.current
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        date_from: dateFrom,
        date_to:   dateTo,
        ...(search        && { search }),
        ...(statusFilter === 'self_delivery' ? { self_delivery: true } : statusFilter ? { status: statusFilter } : {}),
      })
      const res = await api.get(`/orders/admin/orders?${params}`)
      if (id !== fetchIdRef.current) return
      setOrders(res.data.orders      || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
      setStats(res.data.stats        || { total:0, pending:0, processing:0, delivered:0, cancelled:0, revenue:0 })
    } catch {
      if (id !== fetchIdRef.current) return
      setError('Failed to load — showing mock data')
      const statuses = ['pending','confirmed','processing','shipped','delivered','cancelled']
      const mock = Array.from({ length: 16 }, (_, i) => ({
        order_id:       1000 + i,
        buyer_name:     i%5===0 ? null : `Buyer ${i+1}`,
        buyer_phone:    `078${String(i+1).padStart(7,'0')}`,
        shop_name:      `Shop ${(i%4)+1}`,
        status:         statuses[i % 6],
        payment_status: i%4===0 ? 'pending' : 'paid',
        payment_method: ['mtn','airtel','ipay'][i%3],
        total_amount:   Math.floor(Math.random()*30000+5000),
        delivery_fee:   Math.floor(Math.random()*2000+500),
        item_count:     Math.floor(Math.random()*4)+1,
        driver_name:    i%4===0 ? null : `Driver ${(i%3)+1}`,
        delivery_status: i%4===0 ? null : ['assigned','picked_up','delivered'][i%3],
        created_at:     new Date(Date.now()-i*86400000*2).toISOString(),
      }))
      setOrders(mock)
      setTotalPages(4); setTotalCount(80)
      setStats({ total:80, pending:12, processing:18, delivered:40, cancelled:10, revenue:2480000 })
    } finally { if (id === fetchIdRef.current) setLoading(false) }
  }, [page, search, statusFilter, dateFrom, dateTo])

  useEffect(() => { fetchOrders() }, [fetchOrders])
  useEffect(() => { setPage(1)   }, [search, statusFilter, dateFrom, dateTo])

  const handleStatusChange = (orderId, newStatus) => {
    setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o))
  }

  const STATUS_TABS = [
    { id: '',           label: 'All' },
    { id: 'pending',    label: 'Pending'    },
    { id: 'confirmed',  label: 'Confirmed'  },
    { id: 'processing', label: 'Processing' },
    { id: 'shipped',    label: 'Shipped'    },
    { id: 'delivered',  label: 'Delivered'  },
    { id: 'cancelled',  label: 'Cancelled'  },
    { id: 'self_delivery', label: 'Self Delivery' },
  ]

  return (
    <div style={{ padding:'28px 32px 60px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px' }}>Order Management</div>
        <div style={{ fontSize:13, color:'#9a9a94', marginTop:4 }}>Track, manage and update all platform orders.</div>
      </div>

      {/* Stats */}
      <div style={{ display:'flex', gap:14, marginBottom:24 }}>
        <StatCard label="Total Orders"  value={stats.total.toLocaleString()}                                    />
        <StatCard label="Pending"       value={stats.pending.toLocaleString()}     accent="#b45309"             />
        <StatCard label="Processing"    value={stats.processing.toLocaleString()}  accent="#7c3aed"             />
        <StatCard label="Delivered"     value={stats.delivered.toLocaleString()}   accent="#15803d"             />
        <StatCard label="Cancelled"     value={stats.cancelled.toLocaleString()}   accent="#b91c1c"             />
        <StatCard label="Revenue"       value={`${(stats.revenue||0).toLocaleString()} RWF`} accent="#e8440a"  />
        {stats.pending_refunds > 0 && (
          <StatCard label="⚠ Refunds" value={stats.pending_refunds} accent="#b91c1c" sub="pending review" />
        )}
      </div>

      {/* Status filter tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:16, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:5, width:'fit-content', flexWrap:'wrap' }}>
        {STATUS_TABS.map(({ id, label }) => (
          <button key={id} onClick={() => setStatusFilter(id)}
            style={{ padding:'7px 14px', borderRadius:7, border:'none', background:statusFilter===id?'#111317':'transparent', color:statusFilter===id?'#fff':'#9a9a94', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", transition:'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Date range + month shortcuts */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'12px 16px', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:12, color:'#9a9a94', fontWeight:600 }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border:'1px solid #e9e9e4', borderRadius:7, padding:'7px 10px', fontSize:12, fontWeight:600, color:'#111317', outline:'none', fontFamily:"'JetBrains Mono',monospace" }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:12, color:'#9a9a94', fontWeight:600 }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border:'1px solid #e9e9e4', borderRadius:7, padding:'7px 10px', fontSize:12, fontWeight:600, color:'#111317', outline:'none', fontFamily:"'JetBrains Mono',monospace" }} />
        </div>
        {[0,1,2].map(n => {
          const d    = new Date(now.getFullYear(), now.getMonth() - n, 1)
          const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
          const to   = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10)
          const lbl  = n === 0 ? 'This month' : d.toLocaleString('en-GB', { month:'short', year:'numeric' })
          const active = dateFrom === from && dateTo === to
          return (
            <button key={n} onClick={() => { setDateFrom(from); setDateTo(to) }}
              style={{ padding:'6px 12px', borderRadius:6, border:`1px solid ${active?'#111317':'#e9e9e4'}`, background:active?'#111317':'#fff', color:active?'#fff':'#6b7280', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              {lbl}
            </button>
          )
        })}
        <div style={{ flex:1 }} />
        <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", whiteSpace:'nowrap' }}>
          {fmt(dateFrom)} – {fmt(dateTo)}
        </div>
      </div>

      {/* Search + refresh toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'12px 16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, background:'#f6f6f3', border:'1px solid #e9e9e4', borderRadius:7, padding:'7px 12px' }}>
          <span style={{ fontSize:14, color:'#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by order ID, buyer, phone or shop…"
            style={{ border:'none', outline:'none', background:'transparent', fontSize:13, color:'#111317', flex:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor:'pointer', color:'#9a9a94', fontSize:16 }}>×</span>}
        </div>
        <button onClick={fetchOrders} style={{ height:36, width:36, borderRadius:7, border:'1px solid #e9e9e4', background:'#fff', cursor:'pointer', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center' }}>↻</button>
        <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", whiteSpace:'nowrap' }}>{totalCount.toLocaleString()} orders</div>
      </div>

      {error && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 16px', marginBottom:14, fontSize:13, color:'#b91c1c' }}>⚠ {error}</div>
      )}

      {/* Table */}
      <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
        <div style={{ display:'grid', gridTemplateColumns:'0.7fr 1.8fr 1.4fr 1fr 1fr 1fr 1fr 0.9fr', padding:'10px 20px', borderBottom:'1px solid #e9e9e4', background:'#f9f9f7' }}>
          {['#','Buyer / Shop','Status','Payment','Items','Total','Driver','Date'].map(h => (
            <div key={h} style={{ fontSize:10, fontWeight:700, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:"'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding:'48px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>Loading orders…</div>
        ) : orders.length === 0 ? (
          <div style={{ padding:'48px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>No orders found.</div>
        ) : (
          orders.map((o, i) => (
            <div key={o.order_id}
              onClick={() => setSelected(o)}
              style={{ display:'grid', gridTemplateColumns:'0.7fr 1.8fr 1.4fr 1fr 1fr 1fr 1fr 0.9fr', padding:'13px 20px', borderBottom:i<orders.length-1?'1px solid #f3f3f0':'none', cursor:'pointer', transition:'background 0.12s', alignItems:'center' }}
              onMouseEnter={e => e.currentTarget.style.background='#fafaf8'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >
              {/* Order # */}
              <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>#{o.order_id}</div>

              {/* Buyer + shop */}
              <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                <div style={{ width:28, height:28, borderRadius:6, background:avatarBg(o.buyer_name||o.buyer_phone||''), display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, flexShrink:0 }}>
                  {initials(o.buyer_name||o.buyer_phone)}
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.buyer_name || 'Anonymous'}</div>
                  <div style={{ fontSize:10, color:'#9a9a94' }}>{o.shop_name || '—'}</div>
                </div>
              </div>

              {/* Order status */}
              <div><OrderBadge status={o.status} /></div>

              {/* Payment */}
              <div>
                <div style={{ fontSize:11, fontWeight:600, fontFamily:"'JetBrains Mono',monospace", color: o.payment_status==='paid'?'#15803d':'#b45309' }}>
                  {(o.payment_status||'—').toUpperCase()}
                </div>
                <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{o.payment_method?.toUpperCase()||'—'}</div>
              </div>

              {/* Items */}
              <div style={{ fontSize:12, fontFamily:"'JetBrains Mono',monospace", fontWeight:600 }}>
                {o.item_count || '—'} {o.item_count===1?'item':'items'}
              </div>

              {/* Total */}
              <div>
                <div style={{ fontSize:12, fontWeight:800, fontFamily:"'JetBrains Mono',monospace" }}>{(o.total_amount||0).toLocaleString()} RWF</div>
                {o.delivery_fee > 0 && <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>+{(o.delivery_fee||0).toLocaleString()} del.</div>}
              </div>

              {/* Driver */}
              <div>
                {o.is_self_delivery
                  ? <div style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#fff3f0', border:'1px solid #fdd0c0', borderRadius:5, padding:'2px 7px' }}>
                      <span style={{ fontSize:12 }}>🛵</span>
                      <span style={{ fontSize:10, fontWeight:700, color:'#e8440a', fontFamily:"'JetBrains Mono',monospace" }}>SELF DELIVERY</span>
                    </div>
                  : o.driver_name
                    ? <div style={{ fontSize:11, fontWeight:600, color:'#15803d', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{o.driver_name}</div>
                    : <span style={{ fontSize:11, color:'#d1d5db', fontStyle:'italic' }}>No driver</span>}
                {!o.is_self_delivery && o.delivery_status && <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{o.delivery_status}</div>}
                {o.refund_status && (
                  <div style={{ fontSize:9, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", marginTop:2, color: o.refund_status==='refunded'?'#15803d':o.refund_status==='pending'?'#b45309':'#6b7280', background: o.refund_status==='refunded'?'#f0fdf4':o.refund_status==='pending'?'#fffbeb':'#f3f4f6', padding:'1px 5px', borderRadius:3 }}>
                    ↩ {o.refund_status}
                  </div>
                )}
              </div>

              {/* Date */}
              <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>{fmt(o.created_at)}</div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>Page {page} of {totalPages} · {totalCount.toLocaleString()} orders</div>
          <div style={{ display:'flex', gap:6 }}>
            <PagBtn label="← Prev" disabled={page===1}           onClick={() => setPage(p=>p-1)} />
            {Array.from({ length:Math.min(5,totalPages) }, (_,i) => {
              const p = Math.max(1,Math.min(page-2,totalPages-4))+i
              return <PagBtn key={p} label={p} active={p===page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page===totalPages}  onClick={() => setPage(p=>p+1)} />
          </div>
        </div>
      )}

      <OrderDrawer
        order={selected}
        onClose={() => setSelected(null)}
        onStatusChange={handleStatusChange}
      />
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth:34, height:34, borderRadius:7, border:'1px solid #e9e9e4', background:active?'#111317':'#fff', color:active?'#fff':disabled?'#9a9a94':'#111317', fontSize:12, fontWeight:700, cursor:disabled?'not-allowed':'pointer', padding:'0 10px', fontFamily:"'Plus Jakarta Sans',sans-serif", opacity:disabled?0.4:1 }}>
      {label}
    </button>
  )
}